/**
 * Checklist dos comentários que o cliente devolveu, agrupado pela parte da
 * máquina a que cada um se refere.
 *
 * Mora na aba Partes do editor: é ali que a adequação é feita, e é ali que
 * perder um comentário custa uma nova rodada de revisão. Por isso o painel
 * abre com as pendências à mostra e só recolhe o que já foi tratado.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FileText,
  Loader2,
  MessageSquareWarning,
  SlashSquare,
} from 'lucide-react';
import { apiService } from '@/services/api';
import type {
  ChecklistRevisao as ChecklistRevisaoDados,
  ItemChecklist,
  StatusComentarioRevisao,
} from '@/types/revisao';
import { STATUS_ITEM_META, TIPO_ANOTACAO_ROTULO } from '@/types/revisao';
import { useI18n } from '@/contexts/i18n-hook';

interface Props {
  apreciacaoId: string;
  /** Quando informado, o painel mostra só os itens desta parte. */
  filtrarParte?: string;
  /** Avisa o editor que o total de pendências mudou (para o selo da aba). */
  onResumo?: (pendentes: number, total: number) => void;
}

const RESOLVIDOS: StatusComentarioRevisao[] = ['feito', 'nao_aplicavel'];

export function ChecklistRevisao({ apreciacaoId, filtrarParte, onResumo }: Props) {
  const { t } = useI18n();
  const [dados, setDados] = useState<ChecklistRevisaoDados | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [recolhidas, setRecolhidas] = useState<Set<string>>(new Set());
  const [editandoResolucao, setEditandoResolucao] = useState<string | null>(null);
  const [rascunhoResolucao, setRascunhoResolucao] = useState('');

  useEffect(() => {
    let vivo = true;
    (async () => {
      setCarregando(true);
      try {
        const res = await apiService.checklistRevisaoApreciacao(apreciacaoId);
        if (!vivo) return;
        setDados(res.success ? res.data ?? null : null);
        setErro(res.success ? null : res.message ?? t('apreciacaoTabelas.checklist.erro.carregar'));
      } catch {
        if (vivo) setErro(t('apreciacaoTabelas.checklist.erro.carregarRevisao'));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [apreciacaoId, t]);

  const grupos = useMemo(() => {
    if (!dados) return [];
    const todos = [
      ...dados.partes,
      ...(dados.semParte.length > 0
        ? [{ parte: '', resumo: contar(dados.semParte), itens: dados.semParte }]
        : []),
    ];
    if (!filtrarParte) return todos;
    return todos.filter((g) => g.parte === filtrarParte);
  }, [dados, filtrarParte]);

  useEffect(() => {
    if (!dados || !onResumo) return;
    const itens = grupos.flatMap((g) => g.itens);
    onResumo(itens.filter((i) => !RESOLVIDOS.includes(i.status)).length, itens.length);
  }, [dados, grupos, onResumo]);

  const atualizar = async (item: ItemChecklist, mudanca: { status?: StatusComentarioRevisao; resolucao?: string }) => {
    setSalvando(item.id);
    try {
      const res = await apiService.atualizarComentarioRevisao(item.id, mudanca);
      if (!res.success) {
        setErro(res.message ?? t('apreciacaoTabelas.checklist.erro.salvar'));
        return;
      }
      setErro(null);
      setDados((prev) => (prev ? aplicarNoChecklist(prev, item.id, mudanca) : prev));
    } catch {
      setErro(t('apreciacaoTabelas.checklist.erro.salvarAlteracao'));
    } finally {
      setSalvando(null);
    }
  };

  if (carregando) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-rg-line dark:border-schmersal-gray-dark
                      bg-white dark:bg-schmersal-gray-darker px-4 py-3 text-[13px] text-rg-ink-4">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('apreciacaoTabelas.checklist.carregando')}
      </div>
    );
  }

  // Sem ciclo aberto o painel não existe — a aba Partes fica como sempre foi.
  if (!dados) return null;

  const { resumo } = dados;
  const pendentes = resumo.total - resumo.feitos - resumo.naoAplicaveis;
  const percentual = resumo.total > 0 ? Math.round(((resumo.total - pendentes) / resumo.total) * 100) : 0;

  return (
    <div className="rounded-xl border border-amber-300/70 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-900/10 overflow-hidden">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 border-b border-amber-200 dark:border-amber-500/30">
        <div className="flex items-center gap-2 min-w-0">
          <MessageSquareWarning className="h-4.5 w-4.5 text-amber-700 dark:text-amber-300 shrink-0" />
          <span className="text-[13px] font-semibold text-rg-ink-1 dark:text-white">
            {t('apreciacaoTabelas.checklist.titulo', { rodada: dados.rodada })}
          </span>
          {dados.guiaOrigem && (
            <span className="text-[12px] text-rg-ink-4 truncate">
              {t('apreciacaoTabelas.checklist.devolvidoNa', { guia: dados.guiaOrigem.numero })}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 ml-auto">
          <div className="h-1.5 w-24 rounded-full bg-amber-200/70 dark:bg-amber-500/20 overflow-hidden">
            <div
              className="h-full rounded-full bg-amber-600 dark:bg-amber-400 transition-[width] duration-300"
              style={{ width: `${percentual}%` }}
            />
          </div>
          <span className="text-[12px] font-semibold tabular-nums text-rg-ink-2 dark:text-white">
            {resumo.total - pendentes}/{resumo.total}
          </span>
          {dados.arquivoUrl && (
            <a
              href={dados.arquivoUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-schmersal-blue dark:text-schmersal-blue-light hover:underline underline-offset-4"
            >
              <FileText className="h-3.5 w-3.5" />
              {t('apreciacaoTabelas.checklist.pdfComentado')}
            </a>
          )}
          <Link
            to={`/revisoes/${dados.revisaoId}`}
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-schmersal-blue dark:text-schmersal-blue-light hover:underline underline-offset-4"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            {t('apreciacaoTabelas.checklist.abrirRevisao')}
          </Link>
        </div>
      </div>

      {erro && (
        <div className="flex items-center gap-2 px-4 py-2 text-[12px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20">
          <AlertCircle className="h-3.5 w-3.5" />
          {erro}
        </div>
      )}

      {grupos.length === 0 ? (
        <p className="px-4 py-3 text-[13px] text-rg-ink-4">{t('apreciacaoTabelas.checklist.vazio')}</p>
      ) : (
        <div className="divide-y divide-amber-200/70 dark:divide-amber-500/20">
          {grupos.map((grupo) => {
            const chave = grupo.parte || '__sem_parte__';
            const pendentesGrupo = grupo.resumo.total - grupo.resumo.feitos - grupo.resumo.naoAplicaveis;
            // Grupo já resolvido nasce fechado; o que falta fazer fica aberto.
            const recolhido = recolhidas.has(chave) || (pendentesGrupo === 0 && !recolhidas.has(`abrir:${chave}`));

            return (
              <div key={chave}>
                <button
                  type="button"
                  onClick={() =>
                    setRecolhidas((prev) => {
                      const proximo = new Set(prev);
                      if (recolhido) {
                        proximo.delete(chave);
                        proximo.add(`abrir:${chave}`);
                      } else {
                        proximo.delete(`abrir:${chave}`);
                        proximo.add(chave);
                      }
                      return proximo;
                    })
                  }
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-amber-100/50 dark:hover:bg-amber-500/10 transition-colors duration-150"
                >
                  {recolhido ? (
                    <ChevronRight className="h-4 w-4 text-rg-ink-4 shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-rg-ink-4 shrink-0" />
                  )}
                  <span className="text-[13px] font-semibold text-rg-ink-1 dark:text-white truncate">
                    {grupo.parte || t('apreciacaoTabelas.checklist.semParte')}
                  </span>
                  <span
                    className={`ml-auto shrink-0 inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold ${
                      pendentesGrupo > 0
                        ? 'bg-amber-200/80 text-amber-900 dark:bg-amber-500/25 dark:text-amber-100'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                    }`}
                  >
                    {pendentesGrupo > 0
                      ? t('apreciacaoTabelas.checklist.aFazer', { qtd: pendentesGrupo })
                      : t('apreciacaoTabelas.checklist.tudoTratado')}
                  </span>
                </button>

                {!recolhido && (
                  <ul className="px-4 pb-3 space-y-2">
                    {grupo.itens.map((item) => (
                      <li
                        key={item.id}
                        className="rounded-lg border border-rg-line dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker p-3"
                      >
                        <div className="flex flex-wrap items-center gap-2 mb-1.5">
                          <span className="text-[11px] font-semibold text-rg-ink-4">
                            {t('apreciacaoTabelas.checklist.itemCabecalho', {
                              indice: item.indice,
                              pagina: item.pagina || '—',
                              tipo: TIPO_ANOTACAO_ROTULO[item.tipo]
                                ? t(`apreciacaoTabelas.checklist.tipoAnotacao.${item.tipo}`)
                                : item.tipo,
                            })}
                          </span>
                          {item.autor && <span className="text-[11px] text-rg-ink-4">· {item.autor}</span>}
                          {item.parteAuto && (
                            <span
                              className="text-[11px] text-rg-ink-4 italic"
                              title={t('apreciacaoTabelas.checklist.parteDeduzidaDica')}
                            >
                              {t('apreciacaoTabelas.checklist.parteDeduzida')}
                            </span>
                          )}
                          <span
                            className={`ml-auto inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold ${STATUS_ITEM_META[item.status].tom}`}
                          >
                            {t(`apreciacaoTabelas.checklist.statusItem.${item.status}`)}
                          </span>
                        </div>

                        {item.trechoDestacado && (
                          <p className="mb-1.5 text-[12px] italic text-rg-ink-3 dark:text-rg-ink-4 border-l-2 border-amber-400 pl-2">
                            “{item.trechoDestacado}”
                          </p>
                        )}

                        <p className="text-[13px] text-rg-ink-1 dark:text-white whitespace-pre-line">{item.texto}</p>

                        {item.resolucao && editandoResolucao !== item.id && (
                          <p className="mt-1.5 text-[12px] text-green-800 dark:text-green-300">
                            <b className="font-semibold">{t('apreciacaoTabelas.checklist.oQueFoiFeito')}</b>{' '}
                            {item.resolucao}
                          </p>
                        )}

                        {editandoResolucao === item.id && (
                          <div className="mt-2 space-y-1.5">
                            <textarea
                              value={rascunhoResolucao}
                              onChange={(e) => setRascunhoResolucao(e.target.value)}
                              rows={2}
                              placeholder={t('apreciacaoTabelas.checklist.placeholderResolucao')}
                              className="w-full px-2.5 py-1.5 rounded-lg border border-rg-line dark:border-schmersal-gray-dark
                                         bg-white dark:bg-schmersal-gray-darkest text-[12px] text-rg-ink-2 dark:text-white
                                         focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40"
                            />
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={async () => {
                                  await atualizar(item, { resolucao: rascunhoResolucao });
                                  setEditandoResolucao(null);
                                }}
                                className="px-2.5 py-1 rounded-md bg-schmersal-blue text-white text-[12px] font-semibold"
                              >
                                {t('comum.acao.salvar')}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditandoResolucao(null)}
                                className="px-2.5 py-1 rounded-md text-[12px] font-medium text-rg-ink-3"
                              >
                                {t('comum.acao.cancelar')}
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          <BotaoStatus
                            ativo={item.status === 'feito'}
                            carregando={salvando === item.id}
                            icone={<Check className="h-3.5 w-3.5" />}
                            rotulo={t('apreciacaoTabelas.checklist.status.feito')}
                            tomAtivo="bg-green-600 text-white"
                            onClick={() => atualizar(item, { status: item.status === 'feito' ? 'pendente' : 'feito' })}
                          />
                          <BotaoStatus
                            ativo={item.status === 'em_andamento'}
                            carregando={salvando === item.id}
                            icone={<Loader2 className="h-3.5 w-3.5" />}
                            rotulo={t('apreciacaoTabelas.checklist.status.fazendo')}
                            tomAtivo="bg-schmersal-blue text-white"
                            onClick={() =>
                              atualizar(item, { status: item.status === 'em_andamento' ? 'pendente' : 'em_andamento' })
                            }
                          />
                          <BotaoStatus
                            ativo={item.status === 'nao_aplicavel'}
                            carregando={salvando === item.id}
                            icone={<SlashSquare className="h-3.5 w-3.5" />}
                            rotulo={t('apreciacaoTabelas.checklist.status.naoSeAplica')}
                            tomAtivo="bg-slate-600 text-white"
                            onClick={() =>
                              atualizar(item, {
                                status: item.status === 'nao_aplicavel' ? 'pendente' : 'nao_aplicavel',
                              })
                            }
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setEditandoResolucao(item.id);
                              setRascunhoResolucao(item.resolucao ?? '');
                            }}
                            className="px-2 py-1 rounded-md text-[12px] font-medium text-schmersal-blue dark:text-schmersal-blue-light hover:underline underline-offset-4"
                          >
                            {item.resolucao
                              ? t('apreciacaoTabelas.checklist.acao.editarResolucao')
                              : t('apreciacaoTabelas.checklist.acao.anotarResolucao')}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BotaoStatus({
  ativo,
  carregando,
  icone,
  rotulo,
  tomAtivo,
  onClick,
}: {
  ativo: boolean;
  carregando: boolean;
  icone: React.ReactNode;
  rotulo: string;
  tomAtivo: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={carregando}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[12px] font-semibold transition-colors duration-150
                  disabled:opacity-60 ${
                    ativo
                      ? tomAtivo
                      : 'border border-rg-line dark:border-schmersal-gray-dark text-rg-ink-3 dark:text-rg-ink-4 hover:bg-rg-head dark:hover:bg-schmersal-gray-darkest'
                  }`}
    >
      {icone}
      {rotulo}
    </button>
  );
}

function contar(itens: ItemChecklist[]) {
  return {
    total: itens.length,
    pendentes: itens.filter((i) => i.status === 'pendente').length,
    emAndamento: itens.filter((i) => i.status === 'em_andamento').length,
    feitos: itens.filter((i) => i.status === 'feito').length,
    naoAplicaveis: itens.filter((i) => i.status === 'nao_aplicavel').length,
  };
}

/** Reflete a mudança no estado local sem refazer a requisição inteira. */
function aplicarNoChecklist(
  atual: ChecklistRevisaoDados,
  itemId: string,
  mudanca: { status?: StatusComentarioRevisao; resolucao?: string },
): ChecklistRevisaoDados {
  const mapear = (i: ItemChecklist): ItemChecklist =>
    i.id === itemId
      ? {
          ...i,
          status: mudanca.status ?? i.status,
          resolucao: mudanca.resolucao !== undefined ? mudanca.resolucao : i.resolucao,
        }
      : i;

  const partes = atual.partes.map((g) => {
    const itens = g.itens.map(mapear);
    return { ...g, itens, resumo: contar(itens) };
  });
  const semParte = atual.semParte.map(mapear);

  return {
    ...atual,
    partes,
    semParte,
    resumo: contar([...partes.flatMap((g) => g.itens), ...semParte]),
  };
}
